import { useCallback, useEffect, useState } from 'react';
import { Wallet } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import RewardCoin from '@/components/Icons/RewardCoin';
import RewardsProgramTermsModal from '@/components/RewardsProgramTermsModal';
import { Tier } from '@/lib/points';
import { IUser } from '@/redux/auth/auth';
import {
  useAcceptRewardsTermsMutation,
  useGetMyPointsSummaryQuery,
} from '@/redux/points/points.api.slice';
import ProfileDialog from '../ProfileDialog';
import HowToEarn from './HowToEarn';
import RedeemPanel from './RedeemPanel';
import HistoryPanel from './HistoryPanel';
import RedemptionPanel from './RedemptionPanel';

const TABS = [
  { value: 'redeem', label: 'Redeem' },
  { value: 'history', label: 'Points History' },
  { value: 'redemptions', label: 'Coupons' },
];

function useRewardsTerms(user: IUser) {
  const [acceptRewardsTerms] = useAcceptRewardsTermsMutation();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!user.rewardsTermsAcceptedAt) setOpen(true);
  }, [user.rewardsTermsAcceptedAt]);
  const accept = useCallback(async () => {
    await acceptRewardsTerms();
    setOpen(false);
  }, [acceptRewardsTerms]);
  return { open, setOpen, accept };
}

function Balance({ points, tier }: { points: number; tier: Tier }) {
  return (
    <div className="flex items-center gap-3">
      <RewardCoin coinType={tier} width={44} height={44} />
      <div>
        <p className="font-display text-4xl font-semibold leading-none tracking-[-0.03em] tabular-nums">
          {points.toLocaleString()}
          <span className="ml-1 text-base font-medium tracking-normal text-muted-foreground">
            pts
          </span>
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          Worth{' '}
          <span className="font-semibold text-green-700">
            ${points.toLocaleString()}
          </span>{' '}
          in Onefinity coupons
        </p>
      </div>
    </div>
  );
}

function WalletBody() {
  const { data, isLoading } = useGetMyPointsSummaryQuery();
  if (isLoading || !data)
    return <Skeleton className="h-14 w-full rounded-lg" />;
  return (
    <div className="flex flex-col gap-5">
      <Balance
        points={data.currentPoints}
        tier={data.highestTier ?? data.tier}
      />
      <HowToEarn />
      <Tabs defaultValue="redeem" className="flex flex-col">
        <TabsList className="grid h-auto w-full grid-cols-3">
          {TABS.map((tab) => (
            <TabsTrigger
              key={tab.value}
              value={tab.value}
              className="whitespace-normal px-2 py-1.5 text-xs leading-tight sm:text-sm"
            >
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="redeem" className="mt-4">
          <RedeemPanel
            currentPoints={data.currentPoints}
            redemptionEligible={data.redemptionEligible}
          />
        </TabsContent>
        <TabsContent value="history" className="mt-4">
          <HistoryPanel />
        </TabsContent>
        <TabsContent value="redemptions" className="mt-4">
          <RedemptionPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/** Owner-only: the wallet opens in a modal so the page stays short. */
export default function RewardsWallet({ user }: { user: IUser }) {
  const terms = useRewardsTerms(user);
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
      <ProfileDialog
        title="Rewards"
        description="Your points balance, redeem coupons, and history"
        fixedHeight
        trigger={
          <Button size="sm">
            <Wallet className="mr-2 h-4 w-4" />
            Redeem &amp; history
          </Button>
        }
      >
        <div data-testid="rewards-wallet">
          <WalletBody />
        </div>
      </ProfileDialog>
      <RewardsProgramTermsModal
        trigger={
          <Button
            size="sm"
            variant="outline"
            className="border-green-300 bg-card text-green-800 hover:border-green-400 hover:bg-green-100 hover:text-green-900"
          >
            Terms &amp; Conditions
          </Button>
        }
      />
      <RewardsProgramTermsModal
        open={terms.open}
        onOpenChange={terms.setOpen}
        onAccept={terms.accept}
      />
    </div>
  );
}
